import CommonTable from "components/ReactTable/Table";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Container,
  Badge,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  fixedWidthCustomStyle,
  DEFAULT_RECORDS_PER_PAGE,
  MASTER_STATUS_OPTIONS,
} from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { useDispatch, useSelector } from "react-redux";
import { getManufacturerList } from "actions/manufacturer";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import { dashboardOdooSparePartsNotSyncedList } from "actions/dashboard";
import { useNavigate } from "react-router-dom";

function OdooSpareParts() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);

  // Manufacturer list (for the filter dropdown)
  const manufacturerList = useSelector(
    (state) => state.manufacturer.manufacturerList
  );

  const [pageNum, setPageNum] = useState(1);
  const [sparePartListState, setSparePartListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchManufacturer: null,
    searchStatus: null,
    filterParams: [],
  });

  // ✅ Store the Odoo API response in local state
  const [odooResponse, setOdooResponse] = useState(null);

  // --- Fetch active manufacturers for the filter dropdown ---
  useEffect(() => {
    const filterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getManufacturerList(dispatch, { filters: JSON.stringify(filterJSON) });
  }, [dispatch]);

  // --- Fetch Odoo spare parts not-synced list ---
  useEffect(() => {
    const fetchData = async () => {
      setSparePartListState((prev) => ({ ...prev, isLoading: true }));

      const filters = filterState.filterParams?.filters;
      const searchString = filterState.filterParams?.searchString || "";

      try {
        const res = await dashboardOdooSparePartsNotSyncedList({
          pageNum,
          searchString,
          filters:
            filters && filters !== "[]" && filters.length > 2
              ? filters
              : undefined,
        });
        setOdooResponse(res?.data || res);
      } catch (err) {
        setOdooResponse({
          code: 500,
          message: "Failed to load spare parts list! Please try again.",
          data: null,
        });
      }
    };

    fetchData();
  }, [pageNum, filterState.filterParams]);

  // --- Sync loader / error state ---
  useEffect(() => {
    if (!odooResponse) return;

    if (odooResponse.code === 200) {
      setSparePartListState({
        isLoading: false,
        error: "",
        noRecords: (odooResponse?.data?.sparePart?.length ?? 0) === 0,
        showLoaderOnClick: false,
      });
    } else if (odooResponse.code || odooResponse.message) {
      setSparePartListState({
        isLoading: false,
        error:
          odooResponse.message ||
          "Failed to load spare parts list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [odooResponse]);

  const formattedData =
    odooResponse?.data?.sparePart?.map((item, index) => ({
      ...item,
      manufacturerName: item?.manufacturer?.name || "",
      srNo:
        (pageNum - 1) *
          (odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) || [];

  const totalRecords = odooResponse?.data?.totalRecords || 0;
  const noOfRecordsPerPage =
    odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE;

  // --- Table columns ---
  const columns = [
    {
      Header: "Sr. no.",
      accessor: "srNo",
      className: "col-srNo",
      centerAlign: true,
    },
    {
      Header: "Item Number",
      accessor: "itemNumber",
      className: "col-itmNo",
      Cell: ({ value }) => (
        <span onClick={(e) => e.stopPropagation()}>{value}</span>
      ),
    },
    {
      Header: "Manufacturer",
      accessor: "manufacturer",
      className: "col-manufacturer",
      Cell: ({ row }) => row.original.manufacturer?.name || "",
    },
    {
      Header: "Description",
      accessor: "description",
      className: "col-desc",
    },
    {
      Header: "Unit Price (SAR)",
      accessor: "price",
      className: "col-price",
      Cell: ({ value }) => <span className="unit-price-cell">{value}</span>,
    },
    {
      Header: "Quantity",
      accessor: "quantity",
      className: "col-quantity",
      Cell: ({ value }) => <span className="quantity-cell">{value}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      className: "col-status",
      centerAlign: true,
      Cell: ({ value }) => (
        <div className="d-flex justify-content-center align-items-center h-100">
          <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
            {value}
          </Badge>
        </div>
      ),
    },
    {
      Header: "Action",
      accessor: "actions",
      className: "col-actions",
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="actions-right d-flex justify-content-start">
          <div>
            <Button
              className="btn-icon icon-info"
              color="link"
              id={`edit${urlEncodedId(row.original.ID)}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                const encodedId = urlEncodedId(row.original.ID);
                window.open(
                  `/admin/spare-parts/${encodedId}/details?edit=true`,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              <i className="tim-icons icon-pencil tim-md" />
            </Button>
            <UncontrolledTooltip
              delay={0}
              target={`edit${urlEncodedId(row.original.ID)}`}
            >
              Edit
            </UncontrolledTooltip>
          </div>
        </div>
      ),
    },
  ];

  // --- Build filter params from UI state ---
  const buildFilterParams = () => {
    const filterParams = [];
    if (filterState.searchManufacturer) {
      filterParams.push({
        field: "ManufacturerID",
        condition: "eq",
        filterValues: [String(filterState.searchManufacturer.ID)],
      });
    }
    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : undefined;
  };

  const handleSearch = () => {
    const searchString = filterState.searchName.trim();

    if (
      !searchString &&
      !filterState.searchManufacturer &&
      !filterState.searchStatus
    )
      return;

    const newParams = {
      filters: buildFilterParams(),
      searchString,
    };

    if (
      JSON.stringify(filterState.filterParams) !== JSON.stringify(newParams)
    ) {
      setSparePartListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
        isLoading: true,
      }));
      setFilterState((prev) => ({ ...prev, filterParams: newParams }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    setSparePartListState((prev) => ({ ...prev, isLoading: true }));
    setFilterState({
      searchName: "",
      searchManufacturer: null,
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleRowClick = (rowData) => {
    const encodedId = urlEncodedId(rowData.ID);
    window.open(
      `/admin/spare-parts/${encodedId}/details`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // Custom renderers for the Status dropdown
  const Option = (props) => (
    <components.Option {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.Option>
  );

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="mb-2">
                 <div className="d-flex flex-column align-items-start">
                <CardTitle tag="h3" className="font-weight-normal">
                  Odoo Spare Parts — Not Synced
                </CardTitle>
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by item no., description, price or quantity"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      classNamePrefix="react-select"
                      options={manufacturerList?.data?.manufacturer}
                      placeholder="Manufacturer"
                      value={filterState.searchManufacturer}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.ID}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchManufacturer: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={fixedWidthCustomStyle()}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={MASTER_STATUS_OPTIONS}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {sparePartListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams?.filters?.length > 0  || filterState.searchName ? (
                      <Button
                        color="default"
                        className="mb-3 filter-btn btn-simple mt-2"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    ) : ""}
                  </div>
                </div>

                {/* Records count */}
                <div className="mt-2 mb-3 mx-1">
                  {totalRecords > 0 && (
                    <span className="font-weight-bold">
                      Total Spare Parts: {totalRecords}
                    </span>
                  )}
                </div>

                {/* Table */}
                {sparePartListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : sparePartListState.error ? (
                  <div className="loaderContainer">
                    {sparePartListState.error}
                  </div>
                ) : sparePartListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No spare parts found.
                  </div>
                ) : (
                  <div className="table-responsive spare-parts-table">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={noOfRecordsPerPage}
                      activePage={pageNum}
                      data={formattedData}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      rowClick={handleRowClick}
                      totalPages={Math.ceil(totalRecords / noOfRecordsPerPage)}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default OdooSpareParts;